/**
 * NodeContextMenu — glassmorphism right-click context menu for map nodes.
 *
 * Actions: Edit, Duplicate, Change Status, Change Phase, Add Child,
 * Add Related, Delete.
 *
 * Submenus for Status and Phase expand on hover.
 * Dismissed on action, Escape, or click-outside.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import {
  Edit3,
  Copy,
  Activity,
  Layers,
  GitBranch,
  Link2,
  RotateCcw,
  Trash2,
} from 'lucide-react';
import type { AdjacencyNode, Status, Phase } from '../../types/adjacency';

// ── Types ──────────────────────────────────────────────────────────────

interface NodeContextMenuProps {
  node: AdjacencyNode;
  /** Position in container-relative pixels. */
  x: number;
  y: number;
  /** Container dimensions for boundary clamping. */
  containerWidth: number;
  containerHeight: number;
  onClose: () => void;
  onEdit: (id: string) => void;
  onDuplicate: (id: string) => void;
  onChangeStatus: (id: string, status: Status) => void;
  onChangePhase: (id: string, phase: Phase) => void;
  onAddChild: (id: string) => void;
  onAddRelated: (id: string) => void;
  onResetPosition: (id: string) => void;
  onDelete: (id: string) => void;
}

const ALL_STATUSES: Status[] = [
  'In Progress', 'On Hold', 'Not Started', 'Proposed/Pending', 'Complete', 'Blocked', 'Completed',
];

const ALL_PHASES: Phase[] = [
  'Discovery', 'Definition', 'Feasibility', 'Development', 'Beta', 'Launch', 'Production',
];

const STATUS_DOTS: Record<string, string> = {
  'In Progress': '#3b82f6',
  'On Hold': '#f59e0b',
  'Not Started': '#94a3b8',
  'Proposed/Pending': '#a78bfa',
  'Complete': '#22c55e',
  'Blocked': '#ef4444',
  'Completed': '#22c55e',
};

// ── Component ──────────────────────────────────────────────────────────

export function NodeContextMenu({
  node,
  x,
  y,
  containerWidth,
  containerHeight,
  onClose,
  onEdit,
  onDuplicate,
  onChangeStatus,
  onChangePhase,
  onAddChild,
  onAddRelated,
  onResetPosition,
  onDelete,
}: NodeContextMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [openSub, setOpenSub] = useState<'status' | 'phase' | null>(null);

  // Clamp position so menu stays within container
  const MENU_W = 192;
  const MENU_H = 300; // approximate max height
  const clampedX = Math.min(x, containerWidth - MENU_W - 8);
  const clampedY = Math.min(y, containerHeight - MENU_H - 8);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Delay listener to avoid the triggering right-click closing it
    const timer = setTimeout(() => {
      window.addEventListener('mousedown', handleClick);
    }, 0);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  const handleAction = useCallback((fn: () => void) => {
    fn();
    onClose();
  }, [onClose]);

  return (
    <div
      ref={menuRef}
      className="absolute z-40"
      style={{ left: clampedX, top: clampedY }}
    >
      <div
        className="w-48 rounded-lg py-1 text-[11px]"
        style={{
          background: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(148,163,184,0.15)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 1px rgba(148,163,184,0.2)',
        }}
      >
        {/* Node name header */}
        <div
          className="px-3 py-1.5 text-[10px] font-semibold truncate"
          style={{ color: 'var(--text-muted)', borderBottom: '1px solid rgba(148,163,184,0.1)' }}
          title={node.name}
        >
          {node.name}
        </div>

        {/* Edit */}
        <MenuItem icon={<Edit3 size={12} />} label="Edit" onClick={() => handleAction(() => onEdit(node.id))} />

        {/* Duplicate */}
        <MenuItem icon={<Copy size={12} />} label="Duplicate" onClick={() => handleAction(() => onDuplicate(node.id))} />

        <Divider />

        {/* Change Status — submenu */}
        <div
          className="relative"
          onMouseEnter={() => setOpenSub('status')}
          onMouseLeave={() => setOpenSub(null)}
        >
          <MenuItem
            icon={<Activity size={12} />}
            label="Change Status"
            hasSubmenu
            onClick={() => setOpenSub(openSub === 'status' ? null : 'status')}
          />
          {openSub === 'status' && (
            <SubMenu
              parentX={clampedX}
              containerWidth={containerWidth}
            >
              {ALL_STATUSES.map(s => (
                <SubMenuItem
                  key={s}
                  label={s}
                  isActive={node.status === s}
                  dot={STATUS_DOTS[s]}
                  onClick={() => handleAction(() => onChangeStatus(node.id, s))}
                />
              ))}
            </SubMenu>
          )}
        </div>

        {/* Change Phase — submenu */}
        <div
          className="relative"
          onMouseEnter={() => setOpenSub('phase')}
          onMouseLeave={() => setOpenSub(null)}
        >
          <MenuItem
            icon={<Layers size={12} />}
            label="Change Phase"
            hasSubmenu
            onClick={() => setOpenSub(openSub === 'phase' ? null : 'phase')}
          />
          {openSub === 'phase' && (
            <SubMenu
              parentX={clampedX}
              containerWidth={containerWidth}
            >
              {ALL_PHASES.map(p => (
                <SubMenuItem
                  key={p}
                  label={p}
                  isActive={node.phase === p}
                  onClick={() => handleAction(() => onChangePhase(node.id, p))}
                />
              ))}
            </SubMenu>
          )}
        </div>

        <Divider />

        {/* Add Child */}
        <MenuItem
          icon={<GitBranch size={12} />}
          label="Add Child"
          hint="Ctrl+Click target"
          onClick={() => handleAction(() => onAddChild(node.id))}
        />

        {/* Add Related */}
        <MenuItem
          icon={<Link2 size={12} />}
          label="Add Related"
          hint="Alt+Click target"
          onClick={() => handleAction(() => onAddRelated(node.id))}
        />

        {/* Reset to Auto Position — only show if node has a manual position */}
        {node.mapPosition && (
          <MenuItem
            icon={<RotateCcw size={12} />}
            label="Reset to Auto"
            hint="Clear position"
            onClick={() => handleAction(() => onResetPosition(node.id))}
          />
        )}

        <Divider />

        {/* Delete */}
        <MenuItem
          icon={<Trash2 size={12} />}
          label="Delete"
          danger
          onClick={() => handleAction(() => onDelete(node.id))}
        />
      </div>
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────

function MenuItem({
  icon,
  label,
  hint,
  danger,
  hasSubmenu,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  hint?: string;
  danger?: boolean;
  hasSubmenu?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-1.5 text-left transition-colors"
      style={{
        color: danger ? '#ef4444' : 'var(--text-secondary)',
      }}
      onMouseOver={(e) => {
        (e.currentTarget as HTMLElement).style.background = danger
          ? 'rgba(239,68,68,0.1)'
          : 'rgba(148,163,184,0.08)';
      }}
      onMouseOut={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'transparent';
      }}
    >
      <span style={{ opacity: 0.7 }}>{icon}</span>
      <span className="flex-1">{label}</span>
      {hint && <span style={{ color: 'var(--text-muted)', fontSize: '9px' }}>{hint}</span>}
      {hasSubmenu && <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>{'\u25B8'}</span>}
    </button>
  );
}

function Divider() {
  return <div className="my-1" style={{ borderTop: '1px solid rgba(148,163,184,0.1)' }} />;
}

function SubMenu({
  parentX,
  containerWidth,
  children,
}: {
  parentX: number;
  containerWidth: number;
  children: React.ReactNode;
}) {
  // Show submenu to the right, unless it would overflow — then show left
  const SUB_W = 160;
  const PARENT_W = 192;
  const openRight = parentX + PARENT_W + SUB_W + 8 < containerWidth;

  return (
    <div
      className="absolute z-50 py-1 rounded-lg"
      style={{
        top: -4,
        ...(openRight ? { left: PARENT_W - 4 } : { right: PARENT_W - 4 }),
        width: SUB_W,
        background: 'rgba(15, 23, 42, 0.95)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid rgba(148,163,184,0.15)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
      }}
    >
      {children}
    </div>
  );
}

function SubMenuItem({
  label,
  isActive,
  dot,
  onClick,
}: {
  label: string;
  isActive: boolean;
  dot?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors"
      style={{
        color: isActive ? '#38bdf8' : 'var(--text-secondary)',
        background: isActive ? 'rgba(56,189,248,0.08)' : 'transparent',
      }}
      onMouseOver={(e) => {
        (e.currentTarget as HTMLElement).style.background = 'rgba(148,163,184,0.08)';
      }}
      onMouseOut={(e) => {
        (e.currentTarget as HTMLElement).style.background = isActive ? 'rgba(56,189,248,0.08)' : 'transparent';
      }}
    >
      {dot && (
        <span className="w-2 h-2 rounded-full shrink-0" style={{ background: dot }} />
      )}
      <span className="flex-1">{label}</span>
      {isActive && <span style={{ fontSize: '10px' }}>{'\u2713'}</span>}
    </button>
  );
}
